import React, { ReactElement, ReactNode, useEffect, useState } from 'react'
import YouTube from 'react-youtube'
import {
  DesktopBodyLarge,
  DesktopTitleLarge,
  MobileBodyLarge,
  MobileTitleLarge,
} from '../../Typography'
import styles from './YoutubeVideos.module.scss'
import { useMatchMedia } from '../../hooks/useMatchMedia'
import { pageBreakpoints } from '../../config/pageBreakpoints'
import { useYouTubeStore } from '../../stores/YouTubeStore'
import { RingLoader } from 'react-spinners'
import { useCookieConsent } from '../../provider/CookieConsentProvider'

interface TextProps {
  isMobile: boolean
  children: ReactNode
  className?: string | undefined
}

const TitleLarge = ({ isMobile, className, children }: TextProps): ReactElement => {
  return isMobile ? (
    <MobileTitleLarge className={className}>{children}</MobileTitleLarge>
  ) : (
    <DesktopTitleLarge className={className}>{children}</DesktopTitleLarge>
  )
}

const BodyLarge = ({ isMobile, className, children }: TextProps): ReactElement => {
  return isMobile ? (
    <MobileBodyLarge className={className}>{children}</MobileBodyLarge>
  ) : (
    <DesktopBodyLarge className={className}>{children}</DesktopBodyLarge>
  )
}

export const YoutubeVideos = (): ReactElement => {
  const isMobile = useMatchMedia(`(max-width: ${pageBreakpoints.lg})`)
  const [videos, setVideos] = useState<string[]>([])
  const { loaded, videoIds, error } = useYouTubeStore()
  const [videosLoading, setVideosLoading] = useState(false)
  const { consent, setConsent } = useCookieConsent()

  useEffect(() => {
    setVideosLoading(!loaded)
    if (loaded) {
      setVideos(videoIds)
    }
  }, [loaded, videoIds])

  return (
    <div className={styles.uploadList}>
      <TitleLarge isMobile={isMobile} className={styles.title}>
        Uploads
      </TitleLarge>
      {videosLoading ? (
        <RingLoader size={isMobile ? '4rem' : '6rem'} color={'#FBFBFB'} />
      ) : error ? (
        <BodyLarge isMobile={isMobile}>
          Die YouTube-Videos konnten aktuell nicht geladen werden. Bitte versuche es später erneut.
        </BodyLarge>
      ) : consent ? (
        <div className={isMobile ? styles.mobileVideoContainer : styles.webVideoContainer}>
          {videos.map((videoId) => (
            <div key={videoId} className={isMobile ? styles.mobileVideo : styles.webVideo}>
              <YouTube videoId={videoId} />
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.cookieWarning}>
          <BodyLarge isMobile={isMobile}>
            Diese eingebetteten Videos können nicht angezeigt werden, da du der Verwendung von
            Cookies und der Datenübertragung an YouTube nicht zugestimmt hast.
          </BodyLarge>
          <BodyLarge isMobile={isMobile}>
            Mit dem Klick auf den unten stehenden Text erklärst du dich einverstanden, dass Inhalte
            von YouTube geladen werden dürfen. Dabei können personenbezogene Daten an YouTube
            übermittelt werden.
          </BodyLarge>
          <div
            onClick={(): void => {
              setConsent(true)
            }}
          >
            <BodyLarge isMobile={isMobile} className={styles.acceptCookies}>
              Cookies für YouTube akzeptieren
            </BodyLarge>
          </div>
        </div>
      )}
    </div>
  )
}
